import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Snowflake, Mail, Send, Database as DbIcon, Trash2 } from "lucide-react";

export default function AdminConfig() {
    const [cfg, setCfg] = useState(null);
    const [gemini, setGemini] = useState("");
    const [cseKey, setCseKey] = useState("");
    const [cseCx, setCseCx] = useState("");
    const [quota, setQuota] = useState(90);
    const [showUnv, setShowUnv] = useState(false);
    const [freeze, setFreeze] = useState(false);
    const [saving, setSaving] = useState(false);

    // SMTP form (Phase 5)
    const [smtp, setSmtp] = useState({
        enabled: false, host: "", port: 587, use_tls: true, use_ssl: false,
        username: "", sender_email: "no-reply@casaorterie.it", sender_name: "Casa Orterie",
    });
    const [smtpPassword, setSmtpPassword] = useState("");
    const [savingSmtp, setSavingSmtp] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);
    const [lastTest, setLastTest] = useState(null);

    const load = () => {
        api.get("/admin/config").then(({ data }) => {
            setCfg(data);
            setCseCx(data.google_cse_cx || "");
            setQuota(data.google_cse_daily_quota);
            setShowUnv(data.show_unvalidated_ai_notes);
            setFreeze(!!data.freeze);
            if (data.smtp) setSmtp((prev) => ({ ...prev, ...data.smtp }));
        });
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleFreeze = async (v) => {
        setFreeze(v);
        try {
            await api.put("/admin/config", { freeze: v });
            toast.success(v ? "Freeze ATTIVATO" : "Freeze disattivato");
            load();
        } catch { toast.error("Errore"); setFreeze(!v); }
    };

    const togglePairings = async (v) => {
        try {
            await api.put("/admin/config", { show_pairings: v });
            toast.success(v ? "Abbinamenti visibili al pubblico" : "Abbinamenti nascosti dal pubblico");
            load();
        } catch { toast.error("Errore"); }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                google_cse_cx: cseCx,
                google_cse_daily_quota: Number(quota),
                show_unvalidated_ai_notes: showUnv,
            };
            if (gemini) payload.gemini_api_key = gemini;
            if (cseKey) payload.google_cse_api_key = cseKey;
            await api.put("/admin/config", payload);
            toast.success("Configurazione salvata");
            setGemini(""); setCseKey("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const saveSmtp = async (e) => {
        e.preventDefault();
        setSavingSmtp(true);
        try {
            const payload = { smtp };
            if (smtpPassword) payload.smtp_password = smtpPassword;
            await api.put("/admin/config", payload);
            toast.success("Configurazione SMTP salvata");
            setSmtpPassword("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally {
            setSavingSmtp(false);
        }
    };

    const sendTest = async () => {
        if (!testTo) { toast.error("Inserisci un indirizzo destinatario"); return; }
        setTesting(true);
        setLastTest(null);
        try {
            const { data } = await api.post("/admin/smtp/test", { to_email: testTo });
            setLastTest(data);
            if (data.success) toast.success("Email di prova inviata");
            else toast.error(`Invio fallito: ${data.smtp_error || "errore sconosciuto"}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setTesting(false);
        }
    };

    if (!cfg) return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;

    return (
        <div className="max-w-2xl" data-testid="admin-config-page">
            <h1 className="text-3xl mb-2">Configurazione</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8 max-w-xl">
                Chiavi AI, ricerca immagini, invio email SMTP. La chiave Gemini personale,
                se impostata, viene persistita in backend/.env (chmod 600) e usata per
                <strong> tutte le operazioni AI</strong> (arricchimento, controllo qualità, sommelier).
                In assenza, si passa automaticamente alla chiave Emergent di piattaforma.
            </p>

            <Section title="Modalità Freeze" icon={Snowflake}>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Blocca sync Mexal (manuale e schedulato), sync prezzi esterno e arricchimenti AI
                    automatici. Le modifiche manuali admin restano possibili.
                </p>
                <label className="flex items-center gap-3 cursor-pointer text-sm">
                    <Switch checked={freeze} onCheckedChange={toggleFreeze} data-testid="config-freeze-switch" />
                    <span>{freeze ? "Freeze ATTIVO" : "Freeze disattivato"}</span>
                </label>
            </Section>

            <form onSubmit={save} className="space-y-8 mt-8">
                <Section title="Chiave Gemini personale (usata per arricchimento e sommelier)">
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        Chiave attuale: <span className="font-mono text-[hsl(var(--foreground))]">
                            {cfg.gemini_api_key_set ? cfg.gemini_api_key_masked : "— (uso chiave Emergent)"}
                        </span>
                    </p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                        Persistita come <code className="font-mono">GEMINI_API_KEY_CUSTOMER</code> in
                        <code className="font-mono ml-1">backend/.env</code>. Ordine priorità per
                        ogni chiamata AI: chiave personale → Emergent LLM (fallback automatico su quota/errore).
                    </p>
                    <Label className="co-caption mb-2 mt-3 block">Nuova chiave (lascia vuoto per non modificare)</Label>
                    <Input
                        type="password" placeholder="AIza…" value={gemini}
                        onChange={(e) => setGemini(e.target.value)}
                        data-testid="config-gemini-key-input" className="bg-white"
                    />
                </Section>

                <Section title="Ricerca immagini · Google Custom Search">
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        API key attuale: <span className="font-mono text-[hsl(var(--foreground))]">
                            {cfg.google_cse_api_key_set ? cfg.google_cse_api_key_masked : "— (non impostata)"}
                        </span>
                    </p>
                    <Label className="co-caption mb-2 block">Nuova API key</Label>
                    <Input type="password" placeholder="AIza…" value={cseKey} onChange={(e) => setCseKey(e.target.value)} data-testid="config-cse-key-input" className="bg-white" />
                    <Label className="co-caption mb-2 block mt-4">Custom Search Engine ID (CX)</Label>
                    <Input placeholder="0123abc:xyz" value={cseCx} onChange={(e) => setCseCx(e.target.value)} data-testid="config-cse-cx-input" className="bg-white" />
                    <Label className="co-caption mb-2 block mt-4">Quota giornaliera (0-10000)</Label>
                    <Input type="number" min="0" max="10000" value={quota} onChange={(e) => setQuota(e.target.value)} data-testid="config-cse-quota-input" className="bg-white w-32" />
                </Section>

                <Section title="Contenuti pubblici">
                    <label className="flex items-center gap-3 cursor-pointer text-sm">
                        <Switch checked={showUnv} onCheckedChange={setShowUnv} data-testid="config-show-unvalidated-switch" />
                        <span>
                            <span className="block">Mostra al pubblico anche le schede in attesa di validazione</span>
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">Se disattivo, le note AI compaiono solo dopo validazione sommelier.</span>
                        </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer text-sm mt-4">
                        <Switch checked={!!cfg?.show_pairings} onCheckedChange={togglePairings} data-testid="config-show-pairings-switch" />
                        <span>
                            <span className="block">Mostra sezione «Abbinamenti» al pubblico</span>
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">Se disattivo, la voce Abbinamenti scompare dalle schede pubbliche ma resta editabile in admin.</span>
                        </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer text-sm mt-4">
                        <Switch
                            checked={!!cfg?.show_availability_to_client}
                            onCheckedChange={(v) => api.put("/admin/config", { show_availability_to_client: !!v }).then(load)}
                            data-testid="config-show-availability-switch"
                        />
                        <span>
                            <span className="block">Mostra disponibilità al cliente</span>
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">
                                Se attivo, la scheda pubblica espone un flag booleano «disponibile / non disponibile» derivato dalla giacenza (mai il numero di bottiglie). Se disattivo, nessuna informazione di disponibilità è pubblica. Vista staff/admin invariata.
                            </span>
                        </span>
                    </label>
                </Section>

                <DataQualityWebSection cfg={cfg} onSaved={load} />

                <RestaurantUrlSection cfg={cfg} onSaved={load} />

                <div>
                    <button type="submit" disabled={saving} className="co-btn-primary" data-testid="config-save-btn">
                        {saving ? "Salvataggio…" : "Salva configurazione"}
                    </button>
                </div>
            </form>

            <ImportSection cfg={cfg} onSaved={load} />

            <form onSubmit={saveSmtp} className="space-y-4 mt-10">
                <Section title="Invio email SMTP" icon={Mail}>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        Se disattivo, le email restano nell'<b>Email Outbox</b> di sviluppo (link cliccabili in admin).
                        Con SMTP attivo e host valorizzato, verifica/reset/cancellazione partono davvero via SMTP;
                        l'outbox mantiene comunque il log con esito di consegna.
                    </p>

                    <label className="flex items-center gap-3 cursor-pointer text-sm">
                        <Switch
                            checked={!!smtp.enabled}
                            onCheckedChange={(v) => setSmtp({ ...smtp, enabled: v })}
                            data-testid="smtp-enabled-switch"
                        />
                        <span>{smtp.enabled ? "SMTP attivo — invio reale" : "SMTP disattivato — outbox dev"}</span>
                    </label>

                    <div className="grid sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                            <Label className="co-caption text-xs mb-1 block">Host</Label>
                            <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.example.com" className="bg-white" data-testid="smtp-host-input" />
                        </div>
                        <div>
                            <Label className="co-caption text-xs mb-1 block">Porta</Label>
                            <Input type="number" min="1" max="65535" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })} className="bg-white" data-testid="smtp-port-input" />
                        </div>
                    </div>

                    <div className="flex items-center gap-6 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Switch checked={!!smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v, use_ssl: v ? false : smtp.use_ssl })} data-testid="smtp-tls-switch" />
                            <span>STARTTLS</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Switch checked={!!smtp.use_ssl} onCheckedChange={(v) => setSmtp({ ...smtp, use_ssl: v, use_tls: v ? false : smtp.use_tls })} data-testid="smtp-ssl-switch" />
                            <span>SSL/TLS implicito</span>
                        </label>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="co-caption text-xs mb-1 block">Username</Label>
                            <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} autoComplete="username" className="bg-white" data-testid="smtp-user-input" />
                        </div>
                        <div>
                            <Label className="co-caption text-xs mb-1 block">Password
                                <span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-2">
                                    attuale: {cfg.smtp_password_set ? cfg.smtp_password_masked : "— non impostata"}
                                </span>
                            </Label>
                            <Input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder={cfg.smtp_password_set ? "•••• (lascia vuoto per non modificare)" : ""} autoComplete="new-password" className="bg-white" data-testid="smtp-password-input" />
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="co-caption text-xs mb-1 block">Email mittente</Label>
                            <Input type="email" value={smtp.sender_email} onChange={(e) => setSmtp({ ...smtp, sender_email: e.target.value })} className="bg-white" data-testid="smtp-sender-email-input" />
                        </div>
                        <div>
                            <Label className="co-caption text-xs mb-1 block">Nome mittente</Label>
                            <Input value={smtp.sender_name} onChange={(e) => setSmtp({ ...smtp, sender_name: e.target.value })} className="bg-white" data-testid="smtp-sender-name-input" />
                        </div>
                    </div>

                    <div className="pt-2">
                        <button type="submit" disabled={savingSmtp} className="co-btn-primary text-sm" data-testid="smtp-save-btn">
                            {savingSmtp ? "Salvataggio…" : "Salva SMTP"}
                        </button>
                    </div>
                </Section>
            </form>

            <div className="mt-6 bg-white border border-[hsl(var(--border))] rounded-sm p-5" data-testid="smtp-test-block">
                <div className="flex items-center gap-2 mb-3">
                    <Send className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                    <h2 className="text-xl">Invia email di prova</h2>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                    Richiede SMTP attivato e configurato. L'esito reale (successo/errore) viene mostrato qui.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="destinatario@esempio.it" className="bg-white sm:flex-1" data-testid="smtp-test-to-input" />
                    <button onClick={sendTest} disabled={testing || !testTo} className="co-btn-ghost text-sm" data-testid="smtp-test-btn">
                        {testing ? "Invio…" : "Invia prova"}
                    </button>
                </div>
                {lastTest && (
                    <div className={`mt-3 text-sm p-2 rounded-sm ${lastTest.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`} data-testid="smtp-test-result">
                        <div>Esito: <b>{lastTest.smtp_status || "—"}</b> (via {lastTest.sent_via})</div>
                        {lastTest.smtp_error && <div className="text-xs mt-1 font-mono break-all">{lastTest.smtp_error}</div>}
                    </div>
                )}
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section className="bg-white border border-[hsl(var(--border))] rounded-sm p-5 space-y-3">
            <div className="flex items-center gap-2">
                {Icon && <Icon className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />}
                <h2 className="text-xl">{title}</h2>
            </div>
            {children}
        </section>
    );
}


function ImportSection({ cfg, onSaved }) {
    const [includeZero, setIncludeZero] = useState(!!cfg.mexal_import_include_zero_stock);
    const [maxNew, setMaxNew] = useState(cfg.mexal_import_max_new ?? 0);
    const [publicSku, setPublicSku] = useState(!!cfg.show_mexal_sku_public);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/config", {
                mexal_import_include_zero_stock: includeZero,
                mexal_import_max_new: Number(maxNew),
                show_mexal_sku_public: publicSku,
            });
            toast.success("Impostazioni import salvate");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setSaving(false); }
    };

    const deleteDemo = async () => {
        setDeleting(true);
        try {
            const { data } = await api.post("/admin/tools/delete-demo-seed");
            toast.success(`${data.deleted} vini demo eliminati`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setDeleting(false); }
    };

    return (
        <form onSubmit={save} className="space-y-4 mt-10">
            <Section title="Import massivo Mexal" icon={DbIcon}>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Regola come si comporta l'import "Sync ora" dalla lista articoli Mexal. Il default
                    è: importare TUTTI gli articoli con giacenza positiva, senza cap.
                </p>

                <label className="flex items-start gap-3 cursor-pointer text-sm">
                    <Switch checked={includeZero} onCheckedChange={setIncludeZero} data-testid="import-include-zero-switch" />
                    <span>
                        <span className="block">Importa anche articoli a giacenza zero</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                            Default OFF: crea vini solo quando la giacenza in magazzino 4 è &gt; 0.
                            Attivalo se vuoi popolare la carta anche per referenze future.
                        </span>
                    </span>
                </label>

                <div>
                    <Label className="co-caption text-xs mb-1 block">Cap nuovi vini per run manuale</Label>
                    <Input type="number" min="0" max="100000" value={maxNew} onChange={(e) => setMaxNew(e.target.value)} className="bg-white w-32" data-testid="import-max-new-input" />
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                        <b>0</b> = illimitato. Usa un cap piccolo (es. 20) solo per prove; per un import
                        reale lascia 0.
                    </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer text-sm pt-2 border-t border-[hsl(var(--border))]">
                    <Switch checked={publicSku} onCheckedChange={setPublicSku} data-testid="import-public-sku-switch" />
                    <span>
                        <span className="block">Mostra codice articolo al pubblico</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                            Default OFF. Con ON, il codice del formato (es. "Cod. 00123") appare
                            anche nelle schede pubbliche e diventa ricercabile dalla ricerca
                            catalogo. Utile se il cliente vuole comunicare i codici ai clienti B2B.
                        </span>
                    </span>
                </label>

                <div className="pt-2">
                    <button type="submit" disabled={saving} className="co-btn-primary text-sm" data-testid="import-save-btn">
                        {saving ? "Salvataggio…" : "Salva impostazioni import"}
                    </button>
                </div>
            </Section>

            <MapDenominazioniSection cfg={cfg} onSaved={onSaved} />

            <Section title="Elimina vini demo" icon={Trash2}>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Rimuove i vini di esempio caricati alla prima installazione (flag
                    <code className="mx-1 text-xs">seed_demo=true</code>).
                    NON tocca i vini importati da Mexal, CSV o PDF, né quelli creati manualmente in seguito.
                </p>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <button type="button" className="co-btn-ghost text-sm text-[hsl(var(--destructive))] inline-flex items-center gap-2" data-testid="delete-demo-btn">
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Elimina vini demo
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="delete-demo-dialog">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare tutti i vini demo?</AlertDialogTitle>
                            <AlertDialogDescription>
                                L'azione elimina definitivamente tutti e solo i vini con flag
                                <code className="mx-1 text-xs">seed_demo=true</code>. Il conteggio
                                esatto viene mostrato in un toast di conferma. L'operazione è irreversibile.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="delete-demo-cancel">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={(e) => { e.preventDefault(); deleteDemo(); }} disabled={deleting} className="bg-[hsl(var(--destructive))] hover:opacity-90" data-testid="delete-demo-confirm">
                                {deleting ? "Eliminazione…" : "Sì, elimina"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </Section>
        </form>
    );
}


function MapDenominazioniSection({ cfg, onSaved }) {
    const [enabled, setEnabled] = useState(!!cfg.show_denominazioni_on_map);
    const [saving, setSaving] = useState(false);
    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/config", { show_denominazioni_on_map: enabled });
            toast.success("Impostazione mappa salvata");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setSaving(false); }
    };
    return (
        <form onSubmit={save} className="space-y-4 mt-6" data-testid="admin-config-map-denom-section">
            <Section title="Mappa · Aree denominazioni">
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Con questo toggle attivo, la mappa pubblica sovrappone alle regioni un layer
                    aggiuntivo con le aree indicative delle denominazioni (Barolo DOCG, Chianti Classico,
                    Champagne AOC, ecc.). Default OFF: mostra solo regioni e province.
                </p>
                <label className="flex items-start gap-3 cursor-pointer text-sm">
                    <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="map-denom-toggle" />
                    <span>
                        <span className="block">Mostra aree denominazioni sulla mappa</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">
                            Le aree sono INDICATIVE (poligoni semplificati generati semi-automaticamente).
                            La UI mostra sempre una piccola nota discreta "aree indicative".
                        </span>
                    </span>
                </label>
                <div className="pt-2">
                    <button type="submit" disabled={saving} className="co-btn-primary text-sm" data-testid="map-denom-save-btn">
                        {saving ? "Salvataggio…" : "Salva"}
                    </button>
                </div>
            </Section>
        </form>
    );
}


/* ------------------------------------------------------------------ */
/*  Fase 6.8 · pt.4 · URL sito principale del ristorante              */
/* ------------------------------------------------------------------ */
function RestaurantUrlSection({ cfg, onSaved }) {
    const [url, setUrl] = useState(cfg?.restaurant_site_url || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setUrl(cfg?.restaurant_site_url || ""); }, [cfg?.restaurant_site_url]);

    const save = async () => {
        const clean = (url || "").trim();
        if (clean && !/^https?:\/\//i.test(clean)) {
            toast.error("L'URL deve iniziare con https:// o http://");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/config", { restaurant_site_url: clean });
            toast.success(clean ? "URL sito ristorante salvato" : "Link ristorante rimosso");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setSaving(false); }
    };
    return (
        <Section title="Sito web del ristorante">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                URL del sito principale di Casa Orterie. Se valorizzato, in
                header (desktop) e nel menu utente compare un bottone
                <b> "Casa Orterie"</b> che apre il sito in nuova scheda.
                Lascia vuoto per nascondere il bottone.
            </p>
            <Label className="co-caption mb-2 block">URL</Label>
            <Input
                type="url"
                placeholder="https://www.casaorterie.it"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="config-restaurant-url-input"
                className="bg-white"
            />
            <div className="pt-2">
                <button type="button" onClick={save} disabled={saving} className="co-btn-primary text-sm" data-testid="config-restaurant-url-save">
                    {saving ? "Salvataggio…" : "Salva URL"}
                </button>
            </div>
        </Section>
    );
}


/* ------------------------------------------------------------------ */
/*  Fase 7.8 · Controllo qualità AI — grounding web                    */
/* ------------------------------------------------------------------ */
function DataQualityWebSection({ cfg, onSaved }) {
    const [enabled, setEnabled] = useState(!!cfg?.data_quality_use_web_grounding);
    const [quota, setQuota] = useState(cfg?.data_quality_web_daily_quota ?? 100);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setEnabled(!!cfg?.data_quality_use_web_grounding);
        setQuota(cfg?.data_quality_web_daily_quota ?? 100);
    }, [cfg?.data_quality_use_web_grounding, cfg?.data_quality_web_daily_quota]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/config", {
                data_quality_use_web_grounding: !!enabled,
                data_quality_web_daily_quota: Math.max(0, Math.min(1000, Number(quota) || 0)),
            });
            toast.success("Impostazioni Controllo qualità AI salvate");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally { setSaving(false); }
    };

    return (
        <Section title="Controllo qualità AI · Ricerca web">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Attiva il tool nativo <b>Google Search</b> di Gemini durante il
                controllo qualità dei dati. Consumo mirato: scan singolo vino
                sempre con web; scan batch solo per vini <em>rari</em>
                (annata &lt; 2015, produttore con meno di 3 etichette in catalogo
                o denominazione fuori dal mapping principale). Se la chiave
                Gemini non supporta il tool o la quota giornaliera è esaurita,
                il check ricade sulla sola conoscenza del modello (fallback
                silenzioso).
            </p>
            <label className="flex items-center gap-3 cursor-pointer text-sm mt-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    data-testid="config-dq-grounding-switch"
                />
                <span>{enabled ? "Ricerca web attiva" : "Solo conoscenza del modello"}</span>
            </label>
            <Label className="co-caption mb-2 mt-4 block">Tetto giornaliero ricerche web (0-1000)</Label>
            <Input
                type="number"
                min="0"
                max="1000"
                value={quota}
                onChange={(e) => setQuota(e.target.value)}
                className="bg-white w-32"
                data-testid="config-dq-quota-input"
            />
            <div className="pt-3">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="co-btn-primary text-sm"
                    data-testid="config-dq-save-btn"
                >
                    {saving ? "Salvataggio…" : "Salva impostazioni"}
                </button>
            </div>
        </Section>
    );
}
